/**
 * Shared toast lifetimes.
 *
 * Sonner only supports a single default duration on `<Toaster>` - there is
 * no per-severity setting - so an app-wide default that suits a success
 * confirmation ("Saved") also governs the message telling someone why their
 * action failed. Admin ran at 3s, which is why workflow errors read as a
 * flash the user never got to act on (WPS-97).
 *
 * The split below is the house rule:
 *
 *   - success  → the `<Toaster>` default, applied to every toast that does
 *                not opt out. The user already knows what they did; the
 *                toast is a receipt, not information.
 *   - error    → stays until dismissed. An error is the only copy explaining
 *                why the thing the user asked for did not happen; it must
 *                survive them looking away.
 *
 * `<Toaster>` carries `closeButton`, so a persistent toast is always
 * dismissible.
 *
 * Only these two tiers exist because only these two are wired up - `success`
 * on the `<Toaster>` default and `error` inside `toastApiError`. Do not add a
 * tier here before something reads it: sonner has no per-severity duration,
 * so an unread constant silently does nothing and misleads the next reader.
 *
 * Prefer an inline, in-context message over a toast whenever the surface
 * that failed is still on screen (a dialog keeps its own error banner) -
 * a toast is the fallback for when there is nowhere better to put it.
 */
export const TOAST_DURATION = {
  success: 5000,
  /**
   * A confirmation the user is expected to actually read - a warranty tier
   * change, say, which alters coverage and price. Long enough to notice, look
   * at, and understand before it clears; still self-dismissing so a run of
   * several does not pile up.
   */
  confirmation: 10000,
  /** Sonner treats `Infinity` as "stay until dismissed". */
  error: Number.POSITIVE_INFINITY,
} as const
